import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { message } from "antd";
import { toggleMultipleGuestsAllowed } from "Services/demoService";
import logger from "utils/logger";
import "./AllowMultipleGuestsModal.css";

/**
 * Derives whether multiple guests are allowed from the demo's max guest limit.
 *
 * @param {number|null|undefined} maxGuestsAllowed - Current max guests allowed for the demo.
 * @returns {boolean} True when more than one guest is allowed.
 */
const isMultipleGuestsFromMax = (maxGuestsAllowed) => {
  const limit = Number(maxGuestsAllowed);
  return !Number.isNaN(limit) && limit > 1;
};

/**
 * Resolves the current multiple-guests flag from demo fields.
 *
 * @param {boolean|undefined} isMultipleGuestsAllowed - Explicit flag from API, if present.
 * @param {number|null|undefined} maxGuestsAllowed - Fallback max guests allowed value.
 * @returns {boolean} Whether multiple guests are currently allowed.
 */
const resolveMultipleGuestsAllowed = (
  isMultipleGuestsAllowed,
  maxGuestsAllowed
) => {
  if (typeof isMultipleGuestsAllowed === "boolean") {
    return isMultipleGuestsAllowed;
  }
  return isMultipleGuestsFromMax(maxGuestsAllowed);
};

/**
 * Host-only modal to allow or decline additional customers joining the live call.
 * Shown after the recording alert modal is dismissed. Highlights the current setting on rejoin.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.show - Whether the modal is visible.
 * @param {Function} props.handleClose - Callback after a successful button choice closes the modal.
 * @param {string} props.demoId - Demo session UUID for the toggle API.
 * @param {number|null|undefined} [props.maxGuestsAllowed] - Current max guests allowed from demo info.
 * @param {boolean|undefined} [props.isMultipleGuestsAllowed] - Current multiple-guests flag from demo info.
 * @param {Function} [props.onMultipleGuestsUpdated] - Called after a successful API update with response data.
 * @returns {JSX.Element} Allow multiple guests modal.
 */
const AllowMultipleGuestsModal = ({
  show,
  handleClose,
  demoId,
  maxGuestsAllowed,
  isMultipleGuestsAllowed: isMultipleGuestsAllowedProp,
  onMultipleGuestsUpdated,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [currentValue, setCurrentValue] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setCurrentValue(
      resolveMultipleGuestsAllowed(
        isMultipleGuestsAllowedProp,
        maxGuestsAllowed
      )
    );
  }, [isMultipleGuestsAllowedProp, maxGuestsAllowed]);

  /**
   * Persists the host's multiple-guests choice and closes the modal on success.
   *
   * @param {boolean} allowMultipleGuests - True for Allow, false for No Thanks.
   * @returns {Promise<void>}
   */
  const handleChoice = useCallback(
    async (allowMultipleGuests) => {
      if (!demoId || isSubmitting) {
        return;
      }

      setIsSubmitting(true);

      try {
        const response = await toggleMultipleGuestsAllowed({
          demo_id: demoId,
          is_multiple_guests_allowed: allowMultipleGuests,
        });
        const updated = response?.data?.data;

        if (updated?.is_multiple_guests_allowed !== undefined) {
          setCurrentValue(updated.is_multiple_guests_allowed);
        } else if (updated?.max_guests_allowed !== undefined) {
          setCurrentValue(isMultipleGuestsFromMax(updated.max_guests_allowed));
        } else {
          setCurrentValue(allowMultipleGuests);
        }

        onMultipleGuestsUpdated?.(updated);
        handleClose();
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          "Failed to update guest limit. Please try again.";
        messageApi.open({
          type: "error",
          content: errorMessage,
          duration: 5,
        });
        logger.error("Failed to toggle multiple guests allowed:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [demoId, handleClose, isSubmitting, messageApi, onMultipleGuestsUpdated]
  );

  return (
    <div className="recording-popup-modal allow-multiple-guests-modal">
      {contextHolder}
      <Modal
        size={"sm"}
        show={show}
        onHide={() => {}}
        animation={false}
        enforceFocus={false}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header className="allow-multiple-guests-modal__header border-0 pb-0">
          <Modal.Title className="w-100 text-center fw-bold">
            Allow other customers to join this call?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p className="text-secondary mb-0" style={{ fontSize: "15px" }}>
            Other customers can potentially join this call while you&apos;re
            live. You will earn 1 Demo commission payment and Sales Commission
            on each sale made
          </p>
        </Modal.Body>
        <Modal.Footer className="allow-multiple-guests-modal__footer justify-content-center border-0 pt-0">
          <Button
            variant="outline-secondary"
            className={`allow-multiple-guests-modal__btn ${
              !currentValue
                ? "allow-multiple-guests-modal__btn--active"
                : ""
            }`}
            disabled={isSubmitting}
            onClick={() => handleChoice(false)}
          >
            No Thanks
          </Button>
          <Button
            variant="outline-secondary"
            className={`allow-multiple-guests-modal__btn ${
              currentValue ? "allow-multiple-guests-modal__btn--active" : ""
            }`}
            disabled={isSubmitting}
            onClick={() => handleChoice(true)}
          >
            Allow
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

AllowMultipleGuestsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  demoId: PropTypes.string.isRequired,
  maxGuestsAllowed: PropTypes.number,
  isMultipleGuestsAllowed: PropTypes.bool,
  onMultipleGuestsUpdated: PropTypes.func,
};

AllowMultipleGuestsModal.defaultProps = {
  maxGuestsAllowed: undefined,
  isMultipleGuestsAllowed: undefined,
  onMultipleGuestsUpdated: undefined,
};

export default AllowMultipleGuestsModal;
